import * as React from 'react';
import {isValidElementType} from 'react-is';

import {prefixer} from 'stylis';
import createEmotion, {type Emotion} from '@emotion/css/create-instance';
import {get, set} from 'lodash';

import {forwardHtmlProps, getDisplayName, toHash} from '../utils';

import {lineClampPolyfill} from './plugins';
import {getBreakpointVariantStylesFromProps} from './features/breakpoints';
import {getAccessibleBaseProps} from './features/accessibleProps';
import {remapStyleTokens} from './features/system';
import {tokens} from '@packages/utilities/styled-tokens';

import type {
  AnyComponent,
  GetClassName,
  GetDefaultVariantProps,
  MergeVariants,
  StyleObject,
  StyleOptions,
  VariantsType,
  ShapedVariants,
  StyledComponentType,
  SCInfo,
  PropsT,
  StyledAtom,
} from './emotion.types';
import type {MergeTwoObjects} from './_tempTypes.types';

// We create our custom emotion instance to namespace our styles
export const customEmotionInstance: Emotion = createEmotion({
  key: 'wf',
  stylisPlugins: [lineClampPolyfill, prefixer],
  /**
   * In production envs, `emotion` uses the `insertRule` API to insert styles into the DOM. This is extremely fast, but not easily editable.
   * In development envs, `emotion` appends `style` tags to the DOM. This is much slower, but easy for emotion to change.
   *
   * It uses `process.env.NODE_ENV !== 'production'` to determine if we're in production or not.
   * @see https://github.com/emotion-js/emotion/blob/%40emotion/css%4011.12.0/packages/css/src/create-instance.ts#L9
   *
   * In CI, our cypress runs using process.env.NODE_ENV === 'test' to determine if we're in a test environment.
   * This means that it is using the `style` appending rules.
   * When combined with complex selectors (e.g. `[class*="foo"]), this is slow enough that it messes with test timings in weird ways.
   * @see https://github.com/webflow/webflow/pull/76484 & https://webflow.slack.com/archives/C06R3QFRYV7/p1733250756248459
   *
   * Therefore, we are manually setting `speedy` to `true` in non-development environments.
   *
   * Note: As of version 11.13.0, `emotion` uses `--condition` flags instead of `process.env.NODE_ENV`.
   * As such, in order to upgrade `emotion` beyond 11.12.0 we will need to work with the build and delivery teams to add condition flags.
   * @see https://nodejs.org/docs/latest-v18.x/api/packages.html#resolving-user-conditions
   */
  speedy: process.env.NODE_ENV !== 'development',
});

const {css: emotionCSS} = customEmotionInstance;

export const cache = customEmotionInstance.cache;
export const injectGlobal = customEmotionInstance.injectGlobal;
export const keyframes = customEmotionInstance.keyframes;
export const cx = customEmotionInstance.cx;

export const css = (args: StyleObject): string => {
  return emotionCSS(
    // @ts-expect-error - TS2345 - Argument of type 'StyleObject' is not assignable to parameter of type '{} | undefined'.
    remapStyleTokens(args)
  );
};

/** Create a cache to hold all of the atoms we have already compiled */
let GLOBAL_ATOM_CACHE: Record<string, ReturnType<typeof createStyleAtom>> = {};

/**
 * Custom styled scrollbars for all styled components.
 */
export const baseScrollbarStyles = {
  '&::-webkit-scrollbar': {
    height: 9,
    width: 9,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: tokens.colors.uiScrollbarThumb,
    backgroundClip: 'padding-box',
    border: '2px solid rgba(0, 0, 0, 0)',
    borderRadius: 10,
    borderWidth: '2px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
    borderRadius: 10,
  },
  '&:hover': {
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: tokens.colors.uiScrollbarThumbHover,
    },
  },
  /**
   * Custom styles for Firefox.
   */
  '@supports (-moz-appearance:none)': {
    scrollbarWidth: 'thin',
    scrollbarColor: `${tokens.colors.uiScrollbarThumb} transparent`,
  },
} as const;

/**
 * Base styles for all styled components.
 */
const baseStyles = css(baseScrollbarStyles);

/* This is used to reset our caches between tests */
export const emotionTestCacheReset = () => {
  GLOBAL_ATOM_CACHE = {};
  customEmotionInstance.flush();
};

// Instatiate our global variant cache.
const isBaseComponent = (Component: string | AnyComponent) =>
  typeof Component === 'string';

/**
 * A utility function used to conditionally create a new CSS class name or return an existing one.
 */
const getClassName: GetClassName = (obj) => {
  if (typeof obj === 'string') {
    return obj;
  }
  if (typeof obj === 'object' && obj !== null && Object.keys(obj).length > 0) {
    return css(obj);
  }
  return '';
};

const getClassNameFromPath = (path: string) => {
  const style = get(GLOBAL_ATOM_CACHE, path);

  if (!style) {
    /**
     * Asking for a style that doesn't exist.
     * Gracefully fallback to no class.
     */
    return '';
  }

  if (typeof style === 'string') {
    return style;
  }

  /**
   * Asking for a style that exists, but hasn't been compiled yet.
   *
   * Compile it, update the atom's style within the cache, and return the className.
   */
  if (
    typeof style === 'object' &&
    style !== null &&
    Object.keys(style).length > 0
  ) {
    const className = css(style);
    set(GLOBAL_ATOM_CACHE, path, className);
    return className;
  }

  return '';
};

/**
 * Gets the compiled CSS className by comparing incoming component props
 * with the various ways emotionStyled handles variants - from defaultVariants,
 * variants, and compoundVariants.
 */
const getActiveVariantClasses = <Variants extends VariantsType>({
  atomHash,
  enhancedProps = {},
}: {
  atomHash: string;
  enhancedProps: PropsT;
}) => {
  const atom = GLOBAL_ATOM_CACHE[atomHash];

  if (!atom || atom.variants === undefined) {
    return [];
  }

  const {variants, compoundVariants} = atom as StyledAtom<Variants>;

  const activeClasses: Array<string> = [];

  for (const variant in variants) {
    if (variant in enhancedProps) {
      // Our component is passing through the variant as a prop
      const propValue = String(enhancedProps[variant]);

      // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
      if (propValue && propValue in variants[variant]) {
        // The value they've given the prop is valid, so add the variant class to the active classes list.
        const derivedClass = getClassNameFromPath(
          `${atomHash}.variants.${variant}.${propValue}`
        );

        activeClasses.push(derivedClass);
      }
    }
  }

  /**
   *  Check to see if any of our compoundVariants should be applied.
   *  Notice that `getCompoundVariantClasses` receives `enhancedProps`, which means that `defaultVariants` have already been applied.
   *  Please be aware that `compoundVariants` will only check for key-value pairs defined in the `variants` object.
   */
  for (const index in compoundVariants) {
    const compoundVariant = compoundVariants[index];
    if (!compoundVariant) {
      continue;
    }

    const {conditions: variantMatches} = compoundVariant;

    /**
     * We only process and add the compoundVariant class to the collection
     * of activeClasses if it matches all the incoming props from the component.
     *
     * For now, we've decided to restrict the scope of variant matching to
     * keys and values defined in options.variants.
     *
     * Opening this up to features like regex matching and general prop
     * condition matching is (incredibly) powerful, but may perhaps
     * disconnect this compoundVariants with variants.
     *
     * @see https://github.com/webflow/webflow/pull/46516#issuecomment-1098300175
     */
    let matches = 0;
    const matchKeys = Object.keys(variantMatches) as Array<
      keyof typeof variantMatches
    >;

    // Only apply compoundVariants if every key is a defined variant.
    if (variants && !matchKeys.every((key) => key in variants)) continue;

    /**
     * Check to see if the desired matches should cause the compound variants' style to be applied.
     */
    for (const variantKey of matchKeys) {
      /**
       * Don't bother doing any work if our prop value isn't a valid option for the variant in question.
       *
       * We will need to change this if we expand '*' to include non-defined variant keys.
       */
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
      if (!(enhancedProps[variantKey] in variants[variantKey])) break;

      /**
       * The variant value(s) that our compound is attempting to match.
       *
       * If this is a string or boolean, we are trying to match a single value or all values (w/ '*' wildcard)
       * If this is an array, we are trying to match a set of values
       */
      const variantValue = variantMatches[variantKey];

      if (
        typeof variantValue === 'string' ||
        typeof variantValue === 'boolean'
      ) {
        /**
         * Wildcard Matching
         * ------
         * If the wildcard matching value of "*" is set, increase our match count.
         * We do this first, since it is the least amount of work for us to do.
         */
        if (variantValue === '*') {
          matches++;
          continue;
        }

        /**
         * Single Value Matching
         * ----
         * A string or boolean can be provided to match a single variant value.
         */
        if (
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
          String(variantValue) in variants[variantKey] &&
          enhancedProps[variantKey] === variantValue
        ) {
          matches++;
          continue;
        }
      } else if (Array.isArray(variantValue)) {
        /**
         * Selective Matching
         * -------
         * An array of values can be provided for selective matching.
         *
         * For example, if we have a variant of "size", with values of
         * "small", "medium", "large"...
         *
         * The user can specify that they only want to match for "small" and
         * "large".
         *
         * The caveat is that all keys within the array must be defined variants.
         */
        if (
          !variantValue.length ||
          variantValue.includes('*') ||
          !variantValue.includes(enhancedProps[variantKey]) ||
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
          !variantValue.every((key) => key in variants[variantKey])
        )
          break;

        matches++;
        continue;
      }
    }

    if (matches === matchKeys.length) {
      /**
       * We have the correct number of conditional matches for our variant.
       * Add it to our class list.
       */
      const derivedClass = getClassNameFromPath(
        `${atomHash}.compoundVariants[${index}].css`
      );
      activeClasses.push(derivedClass);
    }
  }

  return activeClasses;
};

/**
 * Merges together props and defaultVariants
 */
const getDefaultVariantProps: GetDefaultVariantProps = ({
  props = {},
  defaultVariants = {},
  variants,
}) => {
  if (!variants) {
    return props;
  }

  const enhancedProps = {...props};
  for (const variant in defaultVariants) {
    if (variant in variants) {
      const validVariant =
        typeof enhancedProps[variant] === 'boolean'
          ? true
          : // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
            enhancedProps[variant] in variants[variant];

      /* The either passed in an invalid Variant or an undefined one */
      if (enhancedProps[variant] === undefined || !validVariant) {
        /* Fall back on the specified defaultVariant value */
        enhancedProps[variant] = defaultVariants[variant];
      }
    }
  }

  return enhancedProps;
};

/**
 * Merges two sets of variants, priviliging the first set over the second.
 */
const mergeVariants: MergeVariants = (sourceVariants, atomVariants) => {
  const newVariants = {...sourceVariants} as const;

  for (const variant in atomVariants) {
    if (variant in newVariants) {
      // Both our source and our incoming atom have the same variant defined.
      for (const variantKey in atomVariants[variant]) {
        // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
        if (variantKey in newVariants[variant]) {
          /**
           * This variant key exists in both our source and our atom.
           * Merge their styles together, with our new source taking priority.
           *
           * We create a class for each variant so cx() does the heavy lifting of merging styles for us.
           */
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
          const sourceClass = getClassName(newVariants[variant][variantKey]);
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
          const atomClass = getClassName(atomVariants[variant][variantKey]);

          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
          newVariants[variant][variantKey] = cx(atomClass, sourceClass);
        } else {
          /**
           *  This is a new variant key added by our atom.
           *  Because it does not already exist in our source, we do not need to merge or convert it.
           */
          // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'BooleanVariant | MultiVariant'.
          newVariants[variant][variantKey] = atomVariants[variant][variantKey];
        }
      }
    } else {
      /**
       *  This variant is not defined in our source and is coming exclusively from a composed atom.
       *  Copy it wholesale
       */
      // @ts-expect-error - TS2542 - Index signature in type '{ readonly [x: string]: BooleanVariant | MultiVariant; }' only permits reading.
      newVariants[variant] = atomVariants[variant];
    }
  }

  return newVariants;
};

/**
 * Deep merge an atom with its composes chain.
 */
const composeAtoms = <Variants extends VariantsType>(
  style: string,
  styleOptions: StyleOptions<Variants>
) => {
  const {
    variants = {},
    defaultVariants = {},
    compoundVariants = [],
    composes = [],
    componentName,
  } = styleOptions;

  const composedOptions = {
    style,
    variants,
    defaultVariants,
    compoundVariants,
    dataSc: '',
  };

  const baseStylesToCompose: Array<unknown> = [];
  const dataScToCompose = componentName ? [componentName] : [];

  composes.forEach((atom) => {
    /**
     *  Check whether this is an atom itself or a styled component.
     *  If its a StyledComponent, grab that StyledComponent's atom (will recursively create if necessary)
     */
    // @ts-expect-error - TS2339 - Property '__scInfo' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
    if (atom.__scInfo) {
      // @ts-expect-error - TS2339 - Property '__scInfo' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
      const {hash, styler, options} = atom.__scInfo;
      atom = getStyleAtom(hash, styler, options);
    }

    // @ts-expect-error - TS2339 - Property 'style' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
    if (atom.style) {
      // @ts-expect-error - TS2339 - Property 'style' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
      baseStylesToCompose.push(atom.style);
    }

    // @ts-expect-error - TS2339 - Property 'variants' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
    if (atom.variants) {
      composedOptions.variants = mergeVariants(
        composedOptions.variants,
        // @ts-expect-error - Flow has a hard time telling that we have reassigned atom here.
        atom.variants
      );
    }

    // @ts-expect-error - TS2339 - Property 'defaultVariants' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
    if (atom.defaultVariants) {
      // our current atom's defaultVariant will always be more important than defaultVariants coming from composed atoms.
      composedOptions.defaultVariants = {
        // @ts-expect-error - TS2339 - Property 'defaultVariants' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
        ...atom.defaultVariants,
        ...composedOptions.defaultVariants,
      };
    }

    // @ts-expect-error - TS2339 - Property 'compoundVariants' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
    if (atom.compoundVariants) {
      // our current atom's compoundVariants will always be more important than compoundVariants coming from composed atoms.
      composedOptions.compoundVariants = [
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        ...atom.compoundVariants,
        ...composedOptions.compoundVariants,
      ];
    }

    // @ts-expect-error - TS2339 - Property 'dataSc' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
    if (atom.dataSc) {
      // @ts-expect-error - TS2339 - Property 'dataSc' does not exist on type 'StyledAtom | AbstractComponent<PropsT, any> | StyledComponent<PropsT>'.
      dataScToCompose.concat(atom.dataSc);
    }
  });

  if (baseStylesToCompose.length > 0) {
    // @ts-expect-error - TS2540 - Cannot assign to 'style' because it is a read-only property. | TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'ClassNamesArg'.
    composedOptions.style = cx(...baseStylesToCompose, composedOptions.style);
  }

  composedOptions.dataSc = dataScToCompose.join(' ');

  return composedOptions;
};

/**
 * Generates all the the necessary css for an atom to function.
 * This includes generating its own CSS, as well as merging any composed atoms or styled components
 */
const generateAtomCSS = <Variants extends VariantsType>(
  styler: StyleObject,
  options: StyleOptions<Variants>
) => {
  const {variants = {}, compoundVariants = []} = options;

  /**
   * We first generate the base style, variant, and compoundVariants, for the atom we are going to create.
   *
   * We create a class for the base-style on the assumption that this will always be needed.
   */
  const style = getClassName(styler);

  /**
   * Finally, we take our newly created atom and merge it together with its composed ancestors
   */
  const composedStyles = composeAtoms(style, {
    ...options,
    variants,
    compoundVariants,
  });

  return composedStyles;
};

/**
 * Internal implementation layer for creating a style atom.
 *
 * This allows us to create both raw atoms and atoms from StyledComponents without leaking details to users.
 */
const createStyleAtom = <Variants extends VariantsType>({
  styler = {},
  options = {},
  hash,
}: SCInfo<Variants>) => {
  const {
    style = '',
    variants = {},
    defaultVariants = {},
    compoundVariants = [],
    dataSc,
  } = generateAtomCSS(styler, options);

  /** Styled Components come with their own hash */
  const atomHash =
    hash ??
    toHash({
      style,
      variants,
      defaultVariants,
      compoundVariants,
    });

  const atom = (props = {}) => {
    const enhancedProps = getDefaultVariantProps({
      props,
      defaultVariants,
      variants,
    });

    const activeVariantClasses = getActiveVariantClasses<Variants>({
      atomHash,
      enhancedProps,
    });

    return cx(style, activeVariantClasses);
  };

  atom.style = style;
  atom.variants = variants;
  atom.defaultVariants = defaultVariants;
  atom.compoundVariants = compoundVariants;
  atom.hash = atomHash;
  atom.dataSc = dataSc;
  atom.toString = () => atomHash;

  /** Register atom in cache */
  GLOBAL_ATOM_CACHE[atomHash] = atom;

  return atom;
};

/**
 * # Style Atom
 *
 *  A small, portable, and composable piece of CSS (in-JS) that's part of the
 *  emotion styled system.
 *
 *  It's used together with styled under the "composes" option:
 *
 * @example
 *  ```jsx
 *  const blue = styleAtom({ background: 'blue' });
 *  const Button = styled('button')({ padding: 20 }, { composes: [blue] });
 *  ```
 *
 *  You can create and compose as much atoms as you'd like:
 *
 *  @example
 *  ```jsx
 *  const blue = styleAtom({ background: 'blue' });
 *  const muted = styleAtom({ opacity: 0.5 });
 *  const Button = styled('button')({ padding: 20 }, { composes: [blue, opacity] });
 *  ```
 *
 *  Atoms can also compose other atoms:
 *
 *  @example
 *  ```jsx
 *  const blue = styleAtom({ background: 'blue' });
 *  const mutedBlue = styleAtom({ opacity: 0.5 }, { composes: [blue]});
 *  const Button = styled('button')({ padding: 20 }, { composes: [mutedBlue] });
 *  ```
 *
 *  Atoms also support variants:
 *  @example
 *  ```jsx
 *  const blue = styleAtom(
 *   { background: 'blue' },
 *   {
 *     variants: {
 *      size: {
 *        small: {
 *           padding: 10
 *        },
 *        large: {
 *           padding: 30
 *        }
 *      }
 *    }
 *  });
 *  const Button = styled('button')({ padding: 20 }, { composes: [blue] });
 *  ```
 *
 *  Although not advised, it can also be used directly as a className:
 *
 *  @example
 *  ```jsx
 *  const button = styleAtom({ background: 'blue' });
 *  const Button = ({size, ...props}) => <button className={button({ size })} {...props} />
 *  ```
 */
export const styleAtom = <Variants extends VariantsType>(
  styler: StyleObject = {},
  options: StyleOptions<Variants> = {}
) => {
  return createStyleAtom({styler, options});
};

/**
 * Creates or retrieves an existing style atom.
 */
const getStyleAtom = <Variants extends VariantsType>(
  hash: string,
  styler: StyleObject = {},
  options: StyleOptions<Variants> = {}
) => {
  if (GLOBAL_ATOM_CACHE[hash]) {
    return GLOBAL_ATOM_CACHE[hash];
  } else {
    return createStyleAtom({styler, options, hash});
  }
};

export const SELECTOR_BASE = '--styled';

type StyledProps<
  Variants extends VariantsType,
  Component extends AnyComponent,
> = keyof Variants extends string
  ? MergeTwoObjects<
      ShapedVariants<Variants>,
      React.ComponentPropsWithRef<Component>
    >
  : React.ComponentPropsWithRef<Component>;

/**
 * Our custom emotion-powered CSS-in-JS styled library. In addition to using
 * Emotion for compiling CSS, we've added helpful features such as an easier
 * way to create and render variants.
 *
 * @see https://webflow.atlassian.net/wiki/spaces/FOUN/pages/45384584/Styled+-+Rendering+CSS+with+Emotion+2-pager
 */
const styled =
  <Component extends AnyComponent>(Component: Component) =>
  <Variants extends VariantsType>(
    styler: StyleObject = {},
    options: StyleOptions<Variants> = {}
  ): StyledComponentType<StyledProps<Variants, Component>, Variants> => {
    if (!isValidElementType(Component))
      throw new Error(
        `You must specify a valid Component when using emotionStyled. You supplied ${typeof Component}`
      );

    if (
      typeof Component !== 'string' &&
      '__scInfo' in Component &&
      Component.__scInfo
    ) {
      /**
       *  We are building our current component on another styled component.
       *  Add it to the composes array.
       */
      if (Array.isArray(options.composes)) {
        options.composes.push(Component);
      } else {
        options.composes = [Component];
      }
    }

    const hash = toHash({
      styler,
      ...options,
    });

    /**
     *  Make a unique selector for targeting.
     *  This allows other `styled()` calls to target this Component w/ selectors.
     *
     *  If a selector is provided, we'll use that instead. This allows for explicit targeting.
     *
     * We are adding the options.selector here so we can migrate to D2 while maintaining existing selector access to consumers.
     */
    const selectorHash = `${SELECTOR_BASE}-${hash}`;

    const StyledComponent = React.forwardRef<
      React.ComponentRef<Component>,
      StyledProps<Variants, Component>
    >(
      (
        /** Rendering a component as something else is important
         * as it makes it (much) easier to a swap the base HTML element for
         * another for accessibility, interactions, and/or semantic reasons.
         *
         * For example, being able to render a `<Button />` component as a base
         * `<a />` link tag.
         *
         * @see https://webflow.atlassian.net/wiki/spaces/FOUN/pages/54394893/Rendering+as
         */
        {
          /**
           * We spread the props here to copy them into another object so TS doesn't complain that they are read only when we modify props.as
           */
          ...props
        },
        ref
      ) => {
        // preserve any class names applied by consumers
        const {as: asProp, className} = props;
        let RenderedComponent: AnyComponent | string;

        const atom = getStyleAtom(hash, styler, options);
        const accessibleProps = getAccessibleBaseProps(props);

        if (asProp && isBaseComponent(Component)) {
          RenderedComponent = asProp;
          /**
           * Once we reach the bottom of our initial composes chain, we want to switch to the desired as Component.
           * However, in order to prevent an infinite chain, we need to stop our as propagation.
           */
          // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
          props.as = undefined;
        } else {
          RenderedComponent = Component;
        }

        const responsiveVariantStyles = getBreakpointVariantStylesFromProps({
          css,
          props,
          variants: atom.variants,
          defaultVariants: atom.defaultVariants,
        });

        const activeVariantClasses = atom(props);

        /**
         *  If there is an sc-selector class in our inherited classNames, that means another component is styling this one.
         *  We want that component's selector to take priority.
         */
        const hasSelector = className
          ? className.includes(SELECTOR_BASE)
          : false;

        /**
         * Combine incoming 'data-sc' from any ancestor renders with our local atom.dataSc into a single string.
         * The `data-sc` attribute is used for debugging and VERY LIMITED styling purposes.
         * The data selector allows DS to target by componentName even when Styled Components are composed through non-Styled Components.
         * See the interplay between Text.jsx and Link.jsx
         *
         * The .filter(Boolean) is here to ensure that if either is undefined we don't get extra empty spaces.
         */

        const dataSc = [props['data-sc'], atom.dataSc]
          .filter(Boolean)
          .join(' ');

        /**
         * Prevents the baseStyles from being included (and compounded)
         * with cx() compiling...
         */
        const classNameWithoutBaseStyles = className
          ? className.replace(baseStyles, '')
          : '';

        /**
         * All classes are combined together and compiled with cx()...
         */
        const combinedClasses = cx(
          atom.style,
          activeVariantClasses,
          responsiveVariantStyles,
          classNameWithoutBaseStyles,
          {
            [selectorHash]: !hasSelector,
          }
        ).trim();

        /**
         * Lastly, the baseStyles are added back in.
         */
        const finalClasses = `${baseStyles} ${combinedClasses}`;

        const combinedProps = {...props, ...accessibleProps} as const;
        const {filterForwardedProps} = options;
        const cleanedProps = isBaseComponent(RenderedComponent)
          ? forwardHtmlProps(combinedProps, RenderedComponent)
          : filterForwardedProps != null
            ? filterForwardedProps(combinedProps, forwardHtmlProps)
            : combinedProps;

        return (
          <RenderedComponent
            ref={ref}
            {...cleanedProps}
            {...(dataSc ? {'data-sc': dataSc} : {})} // only add data-sc if dataSc is truthy.
            className={finalClasses}
          />
        );
      }
    ) as StyledComponentType<StyledProps<Variants, Component>, Variants>;

    StyledComponent.displayName =
      typeof Component === 'string' ? 'string' : getDisplayName(Component);
    StyledComponent.toString = () => `.${selectorHash}`; // used so other Components can target by a stable classname.
    StyledComponent.__scInfo = {
      hash,
      styler,
      options,
    };
    return StyledComponent;
  };

export default styled;
